/**
 * STICKERS de FamiDex — catálogo de paquetes [H] (global) y stickers poseídos
 * [L] (privados). El progreso por paquete (poseídos / total) es una función
 * PURA (reusa `computeSeriesProgress` del progreso por serie de la colección:
 * misma aritmética robusta owned/total). UPDATE-or-INSERT en las mutaciones
 * (las vistas PowerSync no admiten UPSERT, gotcha #15).
 */
import {
  and,
  asc,
  eq,
  type PowerSyncDrizzleDB,
  sql,
  sticker,
  sticker_pack,
  user_sticker,
} from "@famidex/powersync";
import { computeSeriesProgress } from "../collection/progress";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

/** Paquetes de stickers del catálogo, ordenados. Global (sin userId). */
export function getStickerPacksQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({
      id: sticker_pack.id,
      name: sticker_pack.name,
      description: sticker_pack.description,
      season: sticker_pack.season,
      total_items: sticker_pack.total_items,
      image_url: sticker_pack.image_url,
      sort_order: sticker_pack.sort_order,
    })
    .from(sticker_pack)
    .orderBy(asc(sticker_pack.sort_order), asc(sticker_pack.name));
}

/** Stickers de un paquete, ordenados. Global (sin userId). */
export function getStickersByPackQuery(db: PowerSyncDrizzleDB, packId: string) {
  return db
    .select({
      id: sticker.id,
      pack_id: sticker.pack_id,
      name: sticker.name,
      image_url: sticker.image_url,
      rarity: sticker.rarity,
      sort_order: sticker.sort_order,
    })
    .from(sticker)
    .where(eq(sticker.pack_id, packId))
    .orderBy(asc(sticker.sort_order));
}

/**
 * Stickers que POSEE el usuario, con su metadata de catálogo (join local [L] ×
 * global [H]). Ordenados por favoritos primero y adquisición reciente.
 */
export function getUserStickersQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: user_sticker.id,
      sticker_id: user_sticker.sticker_id,
      is_favorite: user_sticker.is_favorite,
      acquired_at: user_sticker.acquired_at,
      name: sticker.name,
      image_url: sticker.image_url,
      rarity: sticker.rarity,
      pack_id: sticker.pack_id,
    })
    .from(user_sticker)
    .leftJoin(sticker, eq(user_sticker.sticker_id, sticker.id))
    .where(eq(user_sticker.user_id, userId))
    .orderBy(sql`${user_sticker.is_favorite} DESC`, sql`${user_sticker.acquired_at} DESC`);
}

/**
 * Filas base del PROGRESO POR PAQUETE: stickers DISTINTOS poseídos por paquete.
 * Combínalas con `sticker_pack.total_items` vía `computeStickerPackProgress`.
 */
export function getPackOwnedCountsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      pack_id: sticker.pack_id,
      owned: sql<number>`COUNT(DISTINCT ${user_sticker.sticker_id})`,
    })
    .from(user_sticker)
    .innerJoin(sticker, eq(user_sticker.sticker_id, sticker.id))
    .where(eq(user_sticker.user_id, userId))
    .groupBy(sticker.pack_id);
}

/** Progreso de un paquete (poseídos / total). Pura — reusa la aritmética del progreso por serie. */
export function computeStickerPackProgress(owned: number, total: number) {
  return computeSeriesProgress(owned, total);
}

/**
 * Otorga un sticker al usuario (UPDATE-or-INSERT por (user_id, sticker_id)). El
 * servidor reaplica por id con el user_id estampado (aislamiento). Devuelve si
 * fue nuevo (para disparar la animación de "sticker conseguido").
 */
export async function grantStickerMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  stickerId: string,
): Promise<{ id: string; inserted: boolean }> {
  const now = nowISO();
  const existing = await db
    .select({ id: user_sticker.id })
    .from(user_sticker)
    .where(and(eq(user_sticker.user_id, userId), eq(user_sticker.sticker_id, stickerId)))
    .limit(1);
  const current = existing[0];
  if (current) {
    return { id: current.id, inserted: false };
  }
  const id = generateUUID();
  await db.insert(user_sticker).values({
    id,
    user_id: userId,
    sticker_id: stickerId,
    is_favorite: 0,
    acquired_at: now,
    created_at: now,
    updated_at: now,
  });
  return { id, inserted: true };
}

/** Marca/desmarca un sticker poseído como favorito. */
export async function toggleStickerFavoriteMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
  isFavorite: boolean,
): Promise<void> {
  await db
    .update(user_sticker)
    .set({ is_favorite: isFavorite ? 1 : 0, updated_at: nowISO() })
    .where(and(eq(user_sticker.user_id, userId), eq(user_sticker.id, id)));
}
